import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useGlobal } from 'reactn';
import classNames from 'classnames';
import moment from 'moment';
import { Empty } from 'antd';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import TimesheetGridRow from './TimesheetGridRow';
import Button from '../../common/components/Button';
import TimesheetGridRowAddNew from './TimesheetGridRowAddNew';
import Tag from '../../common/components/Tag';
import Tooltip from '../../common/components/Tooltip';
import { GET_AVAILABLE_DELIVERABLES } from '../queries';
import client from '../../../api/graphql/client';
import ProjectBudgetIndicator from './ProjectBudgetIndicator';
import ProjectResourceHoursIndicator from './ProjectResourceHoursIndicator';
import ProjectMilestoneBudgetIndicator from './ProjectMilestoneBudgetIndicator';
import { constructOptions } from './TimesheetGridRowAddNew';
import TaskSelector from '../../common/components/TaskSelector';
import { GET_USER_TIMESHEET } from '../queries';
import { ADD_TIMESHEET_LINEITEM } from '../mutations';
import { getWeekDays } from '../../common';

const GET_TIME_OFF_BALANCE = gql`
  query GetTimeOffBalance(
    $userId: String!
    $timeOffType: String!
    $startDate: String!
    $endDate: String!
  ) {
    user(userId: $userId) {
      timeOffBalances(timeOffType: $timeOffType, startDate: $startDate, endDate: $endDate) {
        timeOffType
        remaining
        rollover
        earned
        taken
      }
    }
  }
`;

const Table = styled.table`
  width: 100%;
  table-layout: fixed;
`;
const Header = styled.thead``;
const HeaderRow = styled.tr``;
const HeaderCol = styled.th`
  padding: 1em;
  border-bottom: 1px solid #c5c5c5;
  ${props => props.showTopBorder && 'border-top: 1px dashed #c5c5c5;'};
  ${props => props.showRightBorder && 'border-right: 1px dashed #c5c5c5;'};
  ${props => props.showLeftBorder && 'border-left: 1px dashed #c5c5c5;'};
  text-align: ${props => (props.align ? props.align : 'initial')};
  width: ${props => props.width};
  &:first-child {
    padding-left: 0;
  }
`;

const Day = styled.div`
  display: flex;
  justify-content: center;
`;

const ProjectNameRow = styled.tr`
  border-top: 1px solid #f1f1f1;
  &:first-child {
    border-top: none;
  }
  > td {
    padding: 1rem 0 0.5rem 0;
    font-weight: bold;
  }
`;

// use lowercase 3 char week day
const sumTotalsForWeekDay = (weekDay, lineItems) =>
  lineItems.reduce((acc, cur) => {
    const weekDayTask = cur[`${weekDay}Task`];
    if (cur.deliverable.ignoreHoursInTotal) {
      return acc;
    }

    if (weekDayTask) {
      return acc + weekDayTask.hours;
    }

    return acc;
  }, 0);

export const constructDailyTotals = lineItems => ({
  mon: sumTotalsForWeekDay('mon', lineItems),
  tue: sumTotalsForWeekDay('tue', lineItems),
  wed: sumTotalsForWeekDay('wed', lineItems),
  thu: sumTotalsForWeekDay('thu', lineItems),
  fri: sumTotalsForWeekDay('fri', lineItems),
  sat: sumTotalsForWeekDay('sat', lineItems),
  sun: sumTotalsForWeekDay('sun', lineItems),
});

const getClassName = (projectId, collapsedProjectIds) => {
  return classNames('transition-opacity duration-300', {
    'opacity-0 h-0 block overflow-hidden': collapsedProjectIds.includes(projectId),
    'opacity-100': !collapsedProjectIds.includes(projectId),
  });
};

// Helper to extract PTO hours grouped by time off type and year
const getTimeOffHoursByYear = (lineItems, endDate) => {
  const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const hoursByTypeAndYear = {};

  lineItems
    .filter(li => li.deliverable.trackAgainstTimeOff)
    .forEach(li => {
      const timeOffType = li.deliverable.trackAgainstTimeOff;
      days.forEach((day, index) => {
        const task = li[`${day}Task`];
        if (task?.hours) {
          const taskDate = moment(endDate).subtract(6 - index, 'days');
          const year = taskDate.format('YYYY');

          if (!hoursByTypeAndYear[timeOffType]) {
            hoursByTypeAndYear[timeOffType] = {};
          }
          if (!hoursByTypeAndYear[timeOffType][year]) {
            hoursByTypeAndYear[timeOffType][year] = 0;
          }
          hoursByTypeAndYear[timeOffType][year] += task.hours;
        }
      });
    });

  return hoursByTypeAndYear;
};

/**
 * @param {Object} props
 * @param {boolean} props.isLocked
 * @param {Array} props.lineItems
 * @param {string} props.timesheetId
 * @param {string} props.endDate
 * @param {Function} props.shouldAllowSubmit
 * @param {string} props.userId
 * @param {string} [props.className] - Optional className prop
 * @param {string} [props.weekEndDay] - Optional weekEndDay from timesheet
 */
const TimesheetGrid = ({
  isLocked,
  lineItems,
  timesheetId,
  endDate,
  shouldAllowSubmit,
  userId,
  className,
  weekEndDay,
}) => {
  const [stateUser] = useGlobal('user');
  const user = userId ? { _id: userId } : stateUser;
  const [timeOffBalances, setTimeOffBalances] = useState([]);

  const dayBeforeStartOfWeek = moment(endDate)
    .startOf('isoweek')
    .subtract(1, 'days')
    .format('YYYYMMDD');

  // Get PTO hours grouped by type and year for year-boundary timesheets
  const hoursByTypeAndYear = getTimeOffHoursByYear(lineItems, endDate);

  useEffect(() => {
    const fetchTimeOffBalances = async () => {
      const balancePromises = [];

      // Query balance for each type+year combination
      Object.entries(hoursByTypeAndYear).forEach(([timeOffType, yearHours]) => {
        Object.entries(yearHours).forEach(([year, hours]) => {
          const yearStart = `${year}0101`;
          // For the current year of the timesheet, use dayBeforeStartOfWeek; otherwise use year end
          const yearEnd =
            year === moment(dayBeforeStartOfWeek).format('YYYY')
              ? dayBeforeStartOfWeek
              : `${year}1231`;

          balancePromises.push(
            client
              .query({
                query: GET_TIME_OFF_BALANCE,
                variables: {
                  userId: user._id,
                  timeOffType,
                  startDate: yearStart,
                  endDate: yearEnd,
                },
              })
              .then(({ data }) => ({
                timeOffType,
                year,
                remaining: data?.user?.timeOffBalances[0]?.remaining ?? 0,
                hours,
              })),
          );
        });
      });

      const balances = await Promise.all(balancePromises);
      setTimeOffBalances(balances);
    };

    if (Object.keys(hoursByTypeAndYear).length > 0) {
      fetchTimeOffBalances();
    } else {
      setTimeOffBalances([]);
    }
  }, [JSON.stringify(hoursByTypeAndYear)]); // eslint-disable-line react-hooks/exhaustive-deps

  // get holidays config
  const [config] = useGlobal('config');
  const holidaysJsonString = config['holidays'];
  let holidaysConfig;
  try {
    if (holidaysJsonString) {
      holidaysConfig = JSON.parse(holidaysJsonString);
    }
  } catch (error) {
    console.error(error);
  }

  const showBudgetUsedOnTs = config['show-budget-used-on-ts'] === 'true';
  const showHoursUsedOnTs = config['show-hours-used-on-ts'] === 'true';
  const showProjectLinks = config['link-project-on-ts'] === 'true';

  const showTimeOffBalances = config['show-timeoff-balances'] === '1';

  const [collapsedProjectIds, setCollapsedProjectIds] = useState([]);
  const useNewTaskSelector = stateUser?.settings?.useNewTaskSelector;
  const { loading: loadingDeliverables, data } = useQuery(GET_AVAILABLE_DELIVERABLES, {
    variables: { user: user._id, endDate },
  });

  const projects = lineItems
    .reduce((acc, cur) => {
      if (acc.find(p => p._id === cur.project._id)) {
        return acc;
      }
      return [
        ...acc,
        {
          ...cur.project,
        },
      ];
    }, [])
    .sort((a, b) => a.name.localeCompare(b.name));

  const projectsWithTasks = projects.map(p => {
    return {
      ...p,
      lineItems: lineItems
        // lineItemIndex is used to determine which item in the timesheet lineItem array to remove.
        // since we are splitting lineItems into project groups, we have to carry over original index in the original lineItems array
        .map((i, index) => ({ ...i, lineItemIndex: index }))
        .filter(i => i.project._id === p._id),
    };
  });

  const handleClick = async item => {
    const deliverableId = item[item.length - 1];
    const projectId = item[0];
    await client.mutate({
      mutation: ADD_TIMESHEET_LINEITEM,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_USER_TIMESHEET,
          variables: {
            endDate: endDate,
            user: user._id,
          },
        },
      ],
      variables: {
        deliverableId,
        timesheetId,
        projectId,
        user: user._id,
      },
    });
  };

  const selectedDeliverableIds = lineItems.map(li => li.deliverable._id);
  const weekDays = getWeekDays(weekEndDay, endDate);

  // timeOffBalances now contains year-specific data: { timeOffType, year, remaining, hours }
  useEffect(() => {
    if (shouldAllowSubmit) {
      const hasInsufficientBalance = timeOffBalances.some(
        ({ hours, remaining }) => hours > remaining,
      );
      shouldAllowSubmit(!hasInsufficientBalance);
    }
  }, [timeOffBalances, shouldAllowSubmit]);

  return (
    <>
      <Table className={className}>
        <Header>
          <HeaderRow>
            <HeaderCol width="initial">Tasks</HeaderCol>
            <HeaderCol width="50px" />
            {weekDays.map((day, index) => {
              let observedHoliday = null;
              const currentDate = moment(endDate).subtract(6 - index, 'days');
              let displayedDay = currentDate.format('ddd D');
              const isWeekend = currentDate.day() === 6 || currentDate.day() === 0;

              let holiday;

              if (holidaysConfig) {
                holiday = holidaysConfig?.holidays?.find(
                  h => h.date === currentDate.format('YYYYMMDD'),
                );
              }

              if (currentDate.format('YYYYMMDD') === '20191122') {
                return (
                  <HeaderCol key={day} width="80px" align="center">
                    <Day>{displayedDay}</Day>
                  </HeaderCol>
                );
              }
              if (currentDate.format('YYYYMMDD') === '20191129') {
                return (
                  <HeaderCol key={day} width="80px" align="center">
                    <Day>
                      <Tooltip title="Day after Thanksgiving">
                        <Tag color="blue">{displayedDay}</Tag>
                      </Tooltip>
                    </Day>
                  </HeaderCol>
                );
              }

              // Check if Saturday and Sunday are adjacent in the weekDays array
              const isSunday = currentDate.day() === 0;
              const isSaturday = currentDate.day() === 6;
              
              // Check if the next day in the array is Sunday (for Saturday's right border)
              const nextDayIndex = index + 1;
              const nextDayIsSunday = nextDayIndex < weekDays.length && 
                moment(endDate).subtract(6 - nextDayIndex, 'days').day() === 0;
              
              // Check if the previous day in the array is Saturday (for Sunday's left border)
              const prevDayIndex = index - 1;
              const prevDayIsSaturday = prevDayIndex >= 0 && 
                moment(endDate).subtract(6 - prevDayIndex, 'days').day() === 6;

              return (
                <HeaderCol
                  key={day}
                  width="80px"
                  align="center"
                  showTopBorder={isWeekend}
                  showRightBorder={isSunday || (isSaturday && !nextDayIsSunday)}
                  showLeftBorder={isSaturday || (isSunday && !prevDayIsSaturday)}
                  className={classNames('', { 'bg-gray-100': isWeekend })}
                >
                  <Day
                    className={classNames({
                      'font-bold': !isWeekend,
                    })}
                  >
                    {observedHoliday || holiday ? (
                      <Tooltip title={holiday.name}>
                        <Tag color="blue">{displayedDay}</Tag>
                      </Tooltip>
                    ) : (
                      displayedDay
                    )}
                  </Day>
                </HeaderCol>
              );
            })}
            {!isLocked && <HeaderCol width="80px" align="center" />}
          </HeaderRow>
        </Header>
        <tbody>
          {lineItems?.length > 0 ? (
            projectsWithTasks.map(p => {
              const milestones = p.lineItems.reduce((acc, cur) => {
                if (acc.find(item => item.name === cur.deliverable.milestoneName)) {
                  return acc;
                }
                return [
                  ...acc,
                  { deliverableId: cur.deliverable._id, name: cur.deliverable.milestoneName },
                ];
              }, []);
              const projectsLineItems = p.lineItems.filter(li => li.project._id === p._id);
              return (
                <React.Fragment key={p._id}>
                  <ProjectNameRow>
                    <td colSpan={9}>
                      <div className="flex flex-row gap-2 items-center justify-start">
                        <div className="flex flex-row gap-2 items-center">
                          {p.projectCode && <div className="font-bold">[{p.projectCode}]</div>}
                          {showProjectLinks ? (
                            <Link 
                              to={`/finance/projects/${p._id}/financials`}
                              className="font-bold text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {p.name}
                            </Link>
                          ) : (
                            <div className="font-bold">{p.name}</div>
                          )}
                          <Tooltip title="Collapse/Expand project tasks">
                            <Button
                              icon={collapsedProjectIds.includes(p._id) ? 'down' : 'up'}
                              size="small"
                              onClick={() =>
                                setCollapsedProjectIds(
                                  collapsedProjectIds.includes(p._id)
                                    ? collapsedProjectIds.filter(id => id !== p._id)
                                    : [...collapsedProjectIds, p._id],
                                )
                              }
                            ></Button>
                          </Tooltip>
                          {!loadingDeliverables && useNewTaskSelector && !isLocked && (
                            <TaskSelector
                              compact
                              type="primary"
                              options={constructOptions(data?.projects, selectedDeliverableIds)}
                              onSelect={selected =>
                                handleClick([selected.project, undefined, selected.task])
                              }
                              defaultSelected={{
                                project: p._id,
                              }}
                            />
                          )}
                          {!isLocked && showBudgetUsedOnTs && (
                            <ProjectBudgetIndicator
                              projectId={p._id}
                              lineItems={projectsLineItems}
                            />
                          )}
                        </div>

                        {!isLocked && showHoursUsedOnTs && (
                          <ProjectResourceHoursIndicator
                            projectId={p._id}
                            userId={user._id}
                            currentTimesheetHours={lineItems
                              .filter(li => li.project._id === p._id)
                              .reduce((acc, cur) => acc + cur.totalHours, 0)}
                            onNoRemainingHours={() => {
                              // console.log('no remaining hours');
                              // if (shouldAllowSubmit) {
                              //   shouldAllowSubmit(false);
                              // }
                            }}
                          />
                        )}
                      </div>
                    </td>
                  </ProjectNameRow>

                  {milestones
                    .sort((a, b) => {
                      const nameA = a.name;
                      const nameB = b.name;
                      if (nameA < nameB) {
                        return -1;
                      }
                      if (nameA > nameB) {
                        return 1;
                      }
                      return 0;
                    })
                    .map(milestone => {
                      const lineItems = p?.lineItems.filter(li => {
                        return li.deliverable.milestoneName === milestone.name;
                      });
                      return (
                        <React.Fragment key={milestone.deliverableId}>
                          <tr className={getClassName(p._id, collapsedProjectIds)}>
                            <td colSpan={9}>
                              <div className="flex flex-row gap-2 items-center min-h-[30px]">
                                <div className="uppercase font-semibold">{milestone.name}</div>
                                {!collapsedProjectIds.includes(p._id) &&
                                  !loadingDeliverables &&
                                  useNewTaskSelector &&
                                  !isLocked && (
                                    <TaskSelector
                                      compact
                                      type="primary"
                                      options={constructOptions(
                                        data?.projects,
                                        selectedDeliverableIds,
                                      )}
                                      onSelect={selected =>
                                        handleClick([selected.project, undefined, selected.task])
                                      }
                                      defaultSelected={{
                                        project: p._id,
                                        category: milestone.name,
                                      }}
                                    />
                                  )}
                                {!isLocked && showBudgetUsedOnTs && (
                                  <ProjectMilestoneBudgetIndicator
                                    projectId={p._id}
                                    milestoneName={milestone.name}
                                    deliverableId={milestone.deliverableId}
                                    milestoneLineItems={lineItems}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                          {lineItems.map((row, index) => (
                            <TimesheetGridRow
                              className={getClassName(p._id, collapsedProjectIds)}
                              isLocked={isLocked || row.status === 'Approved'}
                              endDate={endDate}
                              key={`${p._id}${row.deliverable._id}`}
                              timesheetId={timesheetId}
                              lineItem={row}
                              lineItemIndex={row.lineItemIndex}
                              user={user}
                              showHoursUsedOnTs={showHoursUsedOnTs}
                              weekEndDay={weekEndDay}
                            />
                          ))}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })
          ) : (
            <tr>
              <td colSpan={9}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Your tasks for this timesheet will appear here."
                />
              </td>
            </tr>
          )}
          <TimesheetGridRowAddNew
            selectedDeliverableIds={selectedDeliverableIds}
            loading={loadingDeliverables}
            isLocked={isLocked}
            endDate={endDate}
            timesheetId={timesheetId}
            dailyTotals={constructDailyTotals(lineItems)}
            userId={userId}
            data={data}
            lineItems={lineItems?.length || 0}
            onSubmit={() => {}}
            onSubmitEnd={() => {}}
            weekEndDay={weekEndDay}
          />
        </tbody>
      </Table>
      {!isLocked && showTimeOffBalances && timeOffBalances.length > 0 && (
        <div className="p-2 border-[1px] border-gray-300 bg-gray-100 border-solid rounded-md mb-2">
          <div className="flex flex-row justify-start gap-2">
            <h4 className="font-bold">Time Off</h4>
            <Link to="/user/profile">View all time off balances</Link>
          </div>
          <div className="mb-2 underline">{`As of ${moment(dayBeforeStartOfWeek).format(
            'MM/DD/YYYY',
          )}, you have the following time off balances:`}</div>
          {timeOffBalances
            .filter(b => b.remaining !== undefined)
            .map(b => (
              <div key={`${b.timeOffType}-${b.year}`} className="">
                <span>{`You have ${b.remaining} hours remaining for ${b.timeOffType} in ${b.year}.`}</span>
                <span className="font-bold">
                  {b.hours > b.remaining &&
                    ` You are trying to take ${b.hours} hours but only have ${b.remaining} hours remaining.`}
                </span>
              </div>
            ))}
        </div>
      )}
    </>
  );
};

export default TimesheetGrid;
